/**
 * maskMorph.js — МОРФОЛОГИЯ МАСКИ СЕГМЕНТАЦИИ. [11.1.38]
 *
 * ПОВОД — живой разбор владельца: он обвёл селезёнку контуром в трёх
 * проекциях, нажал «Только орган в 3D» и получил 2,0 мл вместо ~840 см³ и
 * «какие-то контуры» на экране. Всё честно: программа считает РОВНО
 * закрашенное, а закрашена была скорлупка — кольцо по границе органа, да
 * ещё и на единичных срезах. Чтобы получить объём, нужны две операции,
 * которых в проекте не было вовсе (надгробие в index.html 11.0.2 прямо
 * гласит: «морфологии в проекте нет, дописывать под вывеску не стал»):
 *
 *   1. ЗАЛИВКА замкнутого контура — превратить кольцо в тело.
 *   2. ИНТЕРПОЛЯЦИЯ между размеченными срезами — врач обводит каждый
 *      5–10-й срез, промежуточные программа достраивает сама. Без неё
 *      селезёнка = сто обводок руками, то есть часы работы.
 *
 * УСТРОЙСТВО. Здесь только ЧИСТАЯ математика на типизированных массивах:
 * ни DOM, ни cornerstone, ни импортов ядра. Так это проверяется тестом
 * без браузера (доктрина проекта о чистых модулях) и так же переносится
 * на любой источник маски.
 *
 * СИСТЕМА КООРДИНАТ. Маска — линейный массив, индекс = z*nx*ny + y*nx + x.
 * «Срез» здесь — плоскость XY при фиксированном z, то есть аксиальный
 * срез (ось Z объёма). Обводки в сагиттальной и корональной проекциях
 * ложатся в тот же массив, но заливать их по XY бессмысленно — они там
 * тонкие линии; поэтому заливка идёт по той оси, вдоль которой контур
 * замкнут. Ось выбирает вызывающий, по умолчанию Z.
 */

/**
 * ЗАЛИВКА ЗАМКНУТЫХ КОНТУРОВ по срезам вдоль оси.
 *
 * Как: на каждом срезе заливкой ОТ КРАЁВ помечается «наружное» — всё, куда
 * можно дойти от границы среза, не пересекая контур. Что не помечено и не
 * есть сам контур — внутренность; она и закрашивается.
 *
 * ЧЕСТНАЯ ОГОВОРКА, ради которой всё и написано так: если контур НЕ
 * ЗАМКНУТ (в кольце дыра хотя бы в один воксель), заливка вытекает наружу
 * и закрашивает пол-снимка. Молча этого делать нельзя — врач получил бы
 * «печень в селезёнке». Поэтому срез с утечкой ОТМЕНЯЕТСЯ и попадает в
 * отчёт: `протекло` перечисляет номера таких срезов, чтобы врач знал, где
 * замкнуть контур. Признак утечки: залито больше `долиУтечки` площади
 * среза (по умолчанию 60%) — ни один орган в кадре столько не занимает.
 *
 * @param {Uint8Array|Int16Array|ArrayLike<number>} маска линейный массив
 * @param {[number,number,number]} dims [nx, ny, nz]
 * @param {number} индекс номер сегмента (значение в маске)
 * @param {{ось?:0|1|2, долиУтечки?:number}} опции
 * @returns {{залито:number, срезов:number, протекло:number[]}}
 */
export function залитьКонтуры(маска, dims, индекс = 1, опции = {}) {
  // [11.1.40] Вместе с индексами собираются ПРЕЖНИЕ значения: снять их
  // после операции уже нельзя (массив изменён), а копировать всю маску
  // ради отмены — 214 МБ на телефон.
  const итог = { залито: 0, срезов: 0, протекло: [], изменённые: опции.безОтмены ? null : [], прежние: опции.безОтмены ? null : [] };
  if (!маска?.length || !Array.isArray(dims) || dims.length < 3) return итог;
  const ось = [0, 1, 2].includes(опции.ось) ? опции.ось : 2;
  const долиУтечки = Number.isFinite(опции.долиУтечки) ? опции.долиУтечки : 0.6;
  const [nx, ny, nz] = dims.map((д) => Math.max(0, Math.trunc(д)));
  if (!nx || !ny || !nz) return итог;

  // Развёртка «срез по оси» в общий вид: u и v — оси плоскости среза.
  const размеры = [nx, ny, nz];
  const осьU = ось === 0 ? 1 : 0;
  const осьV = ось === 2 ? 1 : 2;
  const nu = размеры[осьU];
  const nv = размеры[осьV];
  const шаг = [1, nx, nx * ny];
  const шагU = шаг[осьU];
  const шагV = шаг[осьV];
  const шагСреза = шаг[ось];
  const nСрезов = размеры[ось];

  const наружное = new Uint8Array(nu * nv);
  const очередь = new Int32Array(nu * nv);

  for (let с = 0; с < nСрезов; с++) {
    const база = с * шагСреза;
    // Есть ли на срезе хоть один воксель контура — иначе нечего заливать.
    let контурных = 0;
    for (let v = 0; v < nv && контурных < 1; v++) {
      for (let u = 0; u < nu; u++) {
        if (маска[база + u * шагU + v * шагV] === индекс) { контурных = 1; break; }
      }
    }
    if (!контурных) continue;

    наружное.fill(0);
    let хвост = 0;
    const положить = (u, v) => {
      const п = v * nu + u;
      if (наружное[п]) return;
      if (маска[база + u * шагU + v * шагV] === индекс) return;   // стена контура
      наружное[п] = 1;
      очередь[хвост++] = п;
    };
    for (let u = 0; u < nu; u++) { положить(u, 0); положить(u, nv - 1); }
    for (let v = 0; v < nv; v++) { положить(0, v); положить(nu - 1, v); }

    for (let голова = 0; голова < хвост; голова++) {
      const п = очередь[голова];
      const u = п % nu;
      const v = (п - u) / nu;
      if (u > 0) положить(u - 1, v);
      if (u < nu - 1) положить(u + 1, v);
      if (v > 0) положить(u, v - 1);
      if (v < nv - 1) положить(u, v + 1);
    }

    // Кандидаты на заливку: не наружное и не сам контур.
    let кандидатов = 0;
    for (let п = 0; п < наружное.length; п++) {
      if (наружное[п]) continue;
      const u = п % nu;
      const v = (п - u) / nu;
      if (маска[база + u * шагU + v * шагV] !== индекс) кандидатов++;
    }
    if (!кандидатов) {
      // [11.1.38] Контур есть, а внутренности нет — значит кольцо РАЗОМКНУТО:
      // заливка от краёв прошла сквозь дыру и съела внутренность. Молчать
      // об этом нельзя, иначе врач жмёт кнопку и не понимает, почему объём
      // не вырос. Срез попадает в отчёт наравне с утечкой.
      итог.протекло.push(с);
      continue;
    }

    if (кандидатов > наружное.length * долиУтечки) {
      итог.протекло.push(с);   // контур разомкнут — срез не трогаем
      continue;
    }

    for (let п = 0; п < наружное.length; п++) {
      if (наружное[п]) continue;
      const u = п % nu;
      const v = (п - u) / nu;
      const i = база + u * шагU + v * шагV;
      // [11.1.40] Изменённые воксели собираются ДЛЯ ОТМЕНЫ: хранить копию
      // всей маски нельзя (512×512×818 = 214 МБ на снимок, телефон такого
      // не переживёт), а список правок — единицы мегабайт.
      if (маска[i] !== индекс) { итог.изменённые?.push(i); итог.прежние?.push(маска[i]); маска[i] = индекс; итог.залито++; }
    }
    итог.срезов++;
  }
  return итог;
}

/**
 * Расстояние со знаком до края фигуры на срезе (chamfer 3-4, два прохода).
 * Внутри фигуры — отрицательное, снаружи — положительное. Нужен для
 * интерполяции: смешивать сами маски нельзя (получится ступенька), а
 * расстояния смешиваются линейно и дают гладкий промежуточный контур.
 * @returns {Float32Array} nu*nv
 */
export function расстояниеСоЗнаком(маска, база, nu, nv, шагU, шагV, индекс) {
  const БОЛЬШОЕ = 1e6;
  const внутри = new Uint8Array(nu * nv);
  for (let v = 0; v < nv; v++) {
    for (let u = 0; u < nu; u++) {
      внутри[v * nu + u] = маска[база + u * шагU + v * шагV] === индекс ? 1 : 0;
    }
  }
  const считать = (цель) => {
    const d = new Float32Array(nu * nv);
    for (let i = 0; i < d.length; i++) d[i] = внутри[i] === цель ? 0 : БОЛЬШОЕ;
    for (let v = 0; v < nv; v++) {
      for (let u = 0; u < nu; u++) {
        const п = v * nu + u;
        let м = d[п];
        if (u > 0) м = Math.min(м, d[п - 1] + 3);
        if (v > 0) м = Math.min(м, d[п - nu] + 3);
        if (u > 0 && v > 0) м = Math.min(м, d[п - nu - 1] + 4);
        if (u < nu - 1 && v > 0) м = Math.min(м, d[п - nu + 1] + 4);
        d[п] = м;
      }
    }
    for (let v = nv - 1; v >= 0; v--) {
      for (let u = nu - 1; u >= 0; u--) {
        const п = v * nu + u;
        let м = d[п];
        if (u < nu - 1) м = Math.min(м, d[п + 1] + 3);
        if (v < nv - 1) м = Math.min(м, d[п + nu] + 3);
        if (u < nu - 1 && v < nv - 1) м = Math.min(м, d[п + nu + 1] + 4);
        if (u > 0 && v < nv - 1) м = Math.min(м, d[п + nu - 1] + 4);
        d[п] = м;
      }
    }
    return d;
  };
  // [11.1.38] ВНИМАНИЕ, порядок именно такой (первая редакция путала, и
  // интерполяция достраивала НОЛЬ срезов — поймано функциональным тестом,
  // а не глазами): считать(1) даёт ноль в самой фигуре и растёт наружу —
  // это расстояние ДО фигуры, годное для точек СНАРУЖИ. считать(0) даёт
  // ноль в фоне и растёт внутрь — расстояние ДО ФОНА, годное для точек
  // ВНУТРИ. Взять их наоборот значит получить нули везде.
  const доФигуры = считать(1);   // для точек снаружи
  const доФона = считать(0);     // для точек внутри
  const знак = new Float32Array(nu * nv);
  for (let i = 0; i < знак.length; i++) {
    знак[i] = внутри[i] ? -доФона[i] : доФигуры[i];
  }
  return знак;
}

/**
 * ИНТЕРПОЛЯЦИЯ МЕЖДУ РАЗМЕЧЕННЫМИ СРЕЗАМИ вдоль оси.
 *
 * Врач размечает каждый N-й срез, промежуточные достраиваются: между двумя
 * соседними размеченными срезами берутся расстояния со знаком и смешиваются
 * по доле пути; отрицательное значение = внутри промежуточного контура.
 *
 * ЧТО ЭТО НЕ ЕСТЬ: это не диагностическая правда, а геометрическая догадка
 * между вашими обводками — ровно так же, как «заполнение между срезами» в
 * других станциях. Чем реже обводки, тем грубее догадка; на резких
 * изменениях формы (ворота селезёнки, полюса почки) размечайте чаще.
 * Пропуски больше `максРазрыв` срезов НЕ достраиваются вовсе: это уже не
 * интерполяция, а выдумывание органа.
 *
 * @returns {{достроено:number, срезов:number, пары:number, пропущено:number[]}}
 */
export function интерполироватьСрезы(маска, dims, индекс = 1, опции = {}) {
  const итог = { достроено: 0, срезов: 0, пары: 0, пропущено: [], изменённые: опции.безОтмены ? null : [], прежние: опции.безОтмены ? null : [] };
  if (!маска?.length || !Array.isArray(dims) || dims.length < 3) return итог;
  const ось = [0, 1, 2].includes(опции.ось) ? опции.ось : 2;
  const максРазрыв = Number.isFinite(опции.максРазрыв) ? опции.максРазрыв : 25;
  const [nx, ny, nz] = dims.map((д) => Math.max(0, Math.trunc(д)));
  if (!nx || !ny || !nz) return итог;

  const размеры = [nx, ny, nz];
  const осьU = ось === 0 ? 1 : 0;
  const осьV = ось === 2 ? 1 : 2;
  const nu = размеры[осьU];
  const nv = размеры[осьV];
  const шаг = [1, nx, nx * ny];
  const шагU = шаг[осьU];
  const шагV = шаг[осьV];
  const шагСреза = шаг[ось];
  const nСрезов = размеры[ось];

  // Какие срезы размечены.
  const размечен = [];
  for (let с = 0; с < nСрезов; с++) {
    const база = с * шагСреза;
    let есть = false;
    for (let v = 0; v < nv && !есть; v++) {
      for (let u = 0; u < nu; u++) {
        if (маска[база + u * шагU + v * шагV] === индекс) { есть = true; break; }
      }
    }
    if (есть) размечен.push(с);
  }
  итог.срезов = размечен.length;
  if (размечен.length < 2) return итог;   // между одним срезом интерполировать не с чем

  for (let k = 0; k + 1 < размечен.length; k++) {
    const с1 = размечен[k];
    const с2 = размечен[k + 1];
    const разрыв = с2 - с1;
    if (разрыв <= 1) continue;              // соседние — достраивать нечего
    if (разрыв - 1 > максРазрыв) { итог.пропущено.push(с1, с2); continue; }
    итог.пары++;
    const d1 = расстояниеСоЗнаком(маска, с1 * шагСреза, nu, nv, шагU, шагV, индекс);
    const d2 = расстояниеСоЗнаком(маска, с2 * шагСреза, nu, nv, шагU, шагV, индекс);
    for (let с = с1 + 1; с < с2; с++) {
      const t = (с - с1) / разрыв;
      const база = с * шагСреза;
      for (let v = 0; v < nv; v++) {
        for (let u = 0; u < nu; u++) {
          const п = v * nu + u;
          const смесь = d1[п] * (1 - t) + d2[п] * t;
          if (смесь < 0) {
            const i = база + u * шагU + v * шагV;
            if (маска[i] !== индекс) { итог.изменённые?.push(i); итог.прежние?.push(маска[i]); маска[i] = индекс; итог.достроено++; }
          }
        }
      }
    }
  }
  return итог;
}

/** Номера срезов вдоль оси, где сегмент присутствует — для отчёта врачу. */
export function размеченныеСрезы(маска, dims, индекс = 1, ось = 2) {
  const [nx, ny, nz] = (dims || []).map((д) => Math.max(0, Math.trunc(д)));
  const итог = [];
  if (!маска?.length || !nx || !ny || !nz) return итог;
  const размеры = [nx, ny, nz];
  const осьU = ось === 0 ? 1 : 0;
  const осьV = ось === 2 ? 1 : 2;
  const шаг = [1, nx, nx * ny];
  for (let с = 0; с < размеры[ось]; с++) {
    const база = с * шаг[ось];
    let есть = false;
    for (let v = 0; v < размеры[осьV] && !есть; v++) {
      for (let u = 0; u < размеры[осьU]; u++) {
        if (маска[база + u * шаг[осьU] + v * шаг[осьV]] === индекс) { есть = true; break; }
      }
    }
    if (есть) итог.push(с);
  }
  return итог;
}


/**
 * [11.1.41] РАМКА СЕГМЕНТА (bounding box) в вокселях.
 *
 * Зачем: поверхность (STL) и любой тяжёлый расчёт нельзя гнать по ВСЕМУ
 * тому. Владелец поймал это на живом — «Поверхность по маске не
 * построилась»: маска 512×512×818 — это 214 млн вокселей, а marching cubes
 * требует под них ещё Int16-копию на 428 МБ; браузер падал по памяти, VTK
 * молча возвращал пустоту. Селезёнка же занимает от всего этого небольшой
 * ящик — по нему и надо считать.
 *
 * @returns {{x0,x1,y0,y1,z0,z1,вокселей}|null} null — сегмент пуст
 */
export function рамкаСегмента(маска, dims, индекс = 1) {
  if (!маска?.length || !Array.isArray(dims)) return null;
  const [nx, ny, nz] = dims.map((д) => Math.trunc(д));
  let x0 = nx, x1 = -1, y0 = ny, y1 = -1, z0 = nz, z1 = -1, счёт = 0;
  for (let i = 0; i < маска.length; i++) {
    if (маска[i] !== индекс) continue;
    const z = Math.floor(i / (nx * ny));
    const rem = i - z * nx * ny;
    const y = Math.floor(rem / nx);
    const x = rem - y * nx;
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
    if (z < z0) z0 = z; if (z > z1) z1 = z;
    счёт++;
  }
  if (!счёт) return null;
  return { x0, x1, y0, y1, z0, z1, вокселей: счёт };
}

/**
 * Вырезать подобъём по рамке с запасом: двоичная маска (1/0) только этого
 * ящика. Запас нужен, чтобы поверхность замкнулась — без него орган,
 * упирающийся в стенку ящика, даёт открытую сетку и негодный STL.
 * @returns {{бин:Uint8Array, dims:[number,number,number], смещение:[number,number,number]}|null}
 */
export function вырезатьПоРамке(маска, dims, индекс = 1, запас = 2) {
  const р = рамкаСегмента(маска, dims, индекс);
  if (!р) return null;
  const [nx, ny, nz] = dims.map((д) => Math.trunc(д));
  const з = Math.max(1, Math.trunc(запас));
  const x0 = Math.max(0, р.x0 - з), x1 = Math.min(nx - 1, р.x1 + з);
  const y0 = Math.max(0, р.y0 - з), y1 = Math.min(ny - 1, р.y1 + з);
  const z0 = Math.max(0, р.z0 - з), z1 = Math.min(nz - 1, р.z1 + з);
  const mx = x1 - x0 + 1, my = y1 - y0 + 1, mz = z1 - z0 + 1;
  const бин = new Uint8Array(mx * my * mz);
  for (let z = z0; z <= z1; z++) {
    for (let y = y0; y <= y1; y++) {
      const строкаИз = z * nx * ny + y * nx;
      const строкаВ = (z - z0) * mx * my + (y - y0) * mx;
      for (let x = x0; x <= x1; x++) {
        if (маска[строкаИз + x] === индекс) бин[строкаВ + (x - x0)] = 1;
      }
    }
  }
  return { бин, dims: [mx, my, mz], смещение: [x0, y0, z0] };
}

/**
 * [11.1.43] ЗОНА РОСТА: маска сегмента, расширенная на N мм.
 *
 * Прямоугольная рамка (правка 11.1.41) оказалась слишком щедрой: владелец
 * обводил селезёнку в трёх проекциях, рамка растянулась на 150×242×150
 * вокселей, и внутри неё оказались и печень, и мышцы спины — рост честно
 * заполнил 70% ящика (1 617 мл, скриншот). Ящик вокруг органа — не орган.
 *
 * Здесь зона — сам орган, «раздутый» на запас: разрешено расти только там,
 * куда можно дойти от разметки за k шагов. Форма зоны повторяет форму
 * органа, поэтому печень за его пределами недостижима, а дорасти вглубь
 * (там, где врач обвёл не всё) можно.
 *
 * @returns {Uint8Array|null} 1 = расти можно; null, если сегмент пуст
 */
export function зонаРоста(маска, dims, индекс = 1, шагов = 8) {
  if (!маска?.length || !Array.isArray(dims)) return null;
  const [nx, ny, nz] = dims.map((д) => Math.trunc(д));
  const nxy = nx * ny;
  let зона = new Uint8Array(маска.length);
  let есть = 0;
  for (let i = 0; i < маска.length; i++) if (маска[i] === индекс) { зона[i] = 1; есть++; }
  if (!есть) return null;
  const k = Math.max(1, Math.trunc(шагов));
  let фронт = [];
  for (let i = 0; i < зона.length; i++) if (зона[i]) фронт.push(i);
  for (let шаг = 0; шаг < k; шаг++) {
    const новый = [];
    for (const i of фронт) {
      const z = Math.floor(i / nxy);
      const rem = i - z * nxy;
      const y = Math.floor(rem / nx);
      const x = rem - y * nx;
      if (x > 0 && !зона[i - 1]) { зона[i - 1] = 1; новый.push(i - 1); }
      if (x < nx - 1 && !зона[i + 1]) { зона[i + 1] = 1; новый.push(i + 1); }
      if (y > 0 && !зона[i - nx]) { зона[i - nx] = 1; новый.push(i - nx); }
      if (y < ny - 1 && !зона[i + nx]) { зона[i + nx] = 1; новый.push(i + nx); }
      if (z > 0 && !зона[i - nxy]) { зона[i - nxy] = 1; новый.push(i - nxy); }
      if (z < nz - 1 && !зона[i + nxy]) { зона[i + nxy] = 1; новый.push(i + nxy); }
    }
    if (!новый.length) break;
    фронт = новый;
  }
  return зона;
}

export default { залитьКонтуры, интерполироватьСрезы, расстояниеСоЗнаком, размеченныеСрезы, рамкаСегмента, вырезатьПоРамке, зонаРоста };
